/**
 * MIDiagnostics — opt-in telemetry / error channel.
 *
 * Consumers subscribe via `MIDiagnostics.subscribe(...)`. SDK code calls
 * `MIDiagnostics.report(...)` from non-fatal failure paths instead of `throw`.
 * Subscribers can ship events to Sentry, Datadog, or just `console`.
 *
 * Off by default — no events fire until at least one subscriber is registered.
 */

export type MIDiagnosticLevel = 'debug' | 'info' | 'warn' | 'error';

export interface MIDiagnosticEvent {
  level: MIDiagnosticLevel;
  source: string;          // Module / class that emitted (e.g. "MIMap", "WMSLayer")
  message: string;
  error?: Error;
  context?: Record<string, any>;
  timestamp: number;
}

export type MIDiagnosticHandler = (event: MIDiagnosticEvent) => void;

export class MIDiagnostics {
  private static handlers: Set<MIDiagnosticHandler> = new Set();
  private static enabledLevels: Set<MIDiagnosticLevel> = new Set(['warn', 'error']);
  private static buffered: MIDiagnosticEvent[] = [];
  private static maxBuffer = 100;

  /** Register a handler. Returns an unsubscribe fn. */
  static subscribe(handler: MIDiagnosticHandler): () => void {
    MIDiagnostics.handlers.add(handler);
    // Replay buffered events when the first subscriber connects.
    if (MIDiagnostics.handlers.size === 1) {
      for (const ev of MIDiagnostics.buffered) {
        try { handler(ev); } catch {}
      }
      MIDiagnostics.buffered = [];
    }
    return () => { MIDiagnostics.handlers.delete(handler); };
  }

  static setEnabledLevels(levels: MIDiagnosticLevel[]): void {
    MIDiagnostics.enabledLevels = new Set(levels);
  }

  static report(
    source: string,
    message: string,
    options?: { level?: MIDiagnosticLevel; error?: Error; context?: Record<string, any> }
  ): void {
    const level = options?.level ?? 'warn';
    if (!MIDiagnostics.enabledLevels.has(level)) return;
    const event: MIDiagnosticEvent = {
      level, source, message, error: options?.error, context: options?.context, timestamp: Date.now(),
    };
    if (MIDiagnostics.handlers.size === 0) {
      // Buffer to a small ring so early-startup events aren't lost
      MIDiagnostics.buffered.push(event);
      if (MIDiagnostics.buffered.length > MIDiagnostics.maxBuffer) MIDiagnostics.buffered.shift();
      return;
    }
    for (const h of MIDiagnostics.handlers) {
      try { h(event); } catch {}
    }
  }

  static debug(source: string, message: string, ctx?: Record<string, any>): void {
    MIDiagnostics.report(source, message, { level: 'debug', context: ctx });
  }
  static info(source: string, message: string, ctx?: Record<string, any>): void {
    MIDiagnostics.report(source, message, { level: 'info', context: ctx });
  }
  static warn(source: string, message: string, error?: Error, ctx?: Record<string, any>): void {
    MIDiagnostics.report(source, message, { level: 'warn', error, context: ctx });
  }
  static error(source: string, message: string, error?: Error, ctx?: Record<string, any>): void {
    MIDiagnostics.report(source, message, { level: 'error', error, context: ctx });
  }

  /** Convenience: pipe everything to `console`. */
  static enableConsole(): () => void {
    return MIDiagnostics.subscribe((ev) => {
      const tag = `[MIGIS ${ev.level} ${ev.source}]`;
      const fn = (console as any)[ev.level] ?? console.log;
      if (ev.error) fn.call(console, tag, ev.message, ev.error, ev.context ?? '');
      else fn.call(console, tag, ev.message, ev.context ?? '');
    });
  }
}
